import React, { useCallback, useEffect, useState } from 'react'
import styles from './Engine.module.css'
import modalStyles from './ThemeSelectorModal.module.css'
import { getIcon } from '../../common/components/icons/utils'
import { Modal } from '../../common/components/Modal/Modal'
import { CloseButton } from '../../common/components/button/CloseButton'
import { useTrapFocus } from '../../hooks/useTrapFocus'
import { t } from 'i18next'
import { getThemesData, ThemeDataItem } from './EngineHelper'

type ThemeSelectorProps = {
  has3D: boolean
  onSelectTheme: (theme: string) => void
  currentLanguage: string
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ has3D, onSelectTheme, currentLanguage }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [themesData, setThemesData] = useState(getThemesData())
  const [selectedTheme, setSelectedTheme] = useState<ThemeDataItem | null>(null)
  const modalRef = useTrapFocus<HTMLDivElement>(!isModalOpen)

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  useEffect(() => {
    setThemesData(getThemesData())
  }, [currentLanguage])

  const handleSelectThemeInternal = useCallback(
    (theme: ThemeDataItem) => () => {
      setSelectedTheme(theme)
      onSelectTheme(theme.value)
      handleClose()
    },
    [onSelectTheme, handleClose],
  )

  return (
    <>
      <div className={styles.removeBottomSpace}>
        <div className={styles.themeLabel}>{t('engine.theme_label', 'Theme')}</div>
        <div className={styles.formThemeSelector} onClick={handleOpenModal}>
          {!selectedTheme ? (
            <>
              <div className={styles.themePlaceholder}>{t('engine.theme_placeholder', 'Select theme')}</div>
              <div className={styles.themePlaceholderChevron}>
                {<span className='iconWrapperMedium'>{getIcon('chevron')}</span>}
              </div>
            </>
          ) : (
            <>
              <ThemeItem onSelectTheme={handleOpenModal} themeItem={selectedTheme} />
            </>
          )}
        </div>
      </div>
      {isModalOpen && (
        <Modal>
          <div className={modalStyles.themeSelectorModal} ref={modalRef}>
            <div className={modalStyles.themeSelectorHeader}>
              <div className={modalStyles.themeSelectorHeaderTitle}>
                <span>{t('engine.theme_modal.title', 'Select story theme')}</span>
              </div>
              <CloseButton autoFocus onClick={handleClose} />
            </div>
            <div className={modalStyles.themeGrid}>
              {themesData.slice(0, 2).map((themeItem) => {
                return <ThemeItem onSelectTheme={handleSelectThemeInternal(themeItem)} themeItem={themeItem} />
              })}
            </div>
            <div className={modalStyles.themeSelectorSubTitle}>
              {t('engine.theme_modal.seppo_themes', 'Seppo themes')}
            </div>
            <div className={modalStyles.themeGrid}>
              {themesData.slice(2, 6).map((themeItem) => {
                return <ThemeItem onSelectTheme={handleSelectThemeInternal(themeItem)} themeItem={themeItem} />
              })}
            </div>
            {has3D && (
              <>
                <div className={modalStyles.themeSelectorSubTitle}>{t('engine.theme_modal.3d_games', '3D Games')}</div>
                <div className={modalStyles.themeSelectorSubText}>
                  {t('engine.theme_modal.only_desktop', 'Only playable on desktop computers')}
                </div>
                <div className={modalStyles.themeGrid}>
                  {themesData.slice(6, 10).map((themeItem) => {
                    return <ThemeItem onSelectTheme={handleSelectThemeInternal(themeItem)} themeItem={themeItem} />
                  })}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}

type ThemeItemProps = {
  themeItem: ThemeDataItem
  onSelectTheme: () => void
}

export const ThemeItem: React.FC<ThemeItemProps> = ({ themeItem, onSelectTheme }) => {
  return (
    <div className={modalStyles.themeItem} onClick={onSelectTheme}>
      <div className={modalStyles.themeItemLeft}>
        <img
          style={themeItem.imageUrl.includes('svg') ? { width: '24px', height: '24px' } : {}}
          src={themeItem.imageUrl}
          alt={themeItem.name}
        />
      </div>
      <div className={modalStyles.themeItemRight}>
        <div className={modalStyles.themeName}>{themeItem.name}</div>
        <div className={modalStyles.themeText}>{themeItem.description}</div>
        {themeItem.focusText && (
          <div className={modalStyles.themeText}>
            <span style={{ fontWeight: 'bold' }}>{t('engine.theme_modal.focus', 'Focus')} </span>
            <span>{themeItem.focusText}</span>
          </div>
        )}
      </div>
    </div>
  )
}
